// react
import React, { useState, useEffect } from 'react';
import EmptyState from '../shared/EmptyState';
import { Link, useHistory } from 'react-router-dom';

// third-party
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import axios from 'axios';

// application
import Pagination from '../shared/Pagination';
import { url } from '../../services/utils';
import theme from '../../data/theme';
import { useOrderTimer } from '../../services/orderTimer';

function OrderItemCard({ item, formatRupiah, token }) {
    const history = useHistory();
    const isUnpaid = item.active_flag === 1;
    const timer = useOrderTimer(item.id, item.transaction_code, item.transaction_date);

    const [productItems, setProductItems] = useState(item.productItems || null);
    const [loadingProducts, setLoadingProducts] = useState(!item.productItems);

    useEffect(() => {
        let isMounted = true;
        if (item.productItems) {
            setProductItems(item.productItems);
            setLoadingProducts(false);
            return;
        }

        const fetchCardProducts = async () => {
            const activeToken = token || localStorage.getItem('token');
            if (!activeToken || !item.transaction_code) return;
            try {
                setLoadingProducts(true);
                const detailRes = await axios.get(
                    `${process.env.REACT_APP_HOST_API}/transaction/detail/${item.transaction_code}`,
                    {
                        headers: {
                            Authorization: `Bearer ${activeToken}`,
                        },
                        timeout: 6000,
                    }
                );
                const soId = detailRes.data?.response?.soCommerceDetail?.id;
                if (soId && isMounted) {
                    const itemsRes = await axios.post(
                        `${process.env.REACT_APP_HOST_API}/so-commerce/item`,
                        { sc_id: soId },
                        {
                            headers: {
                                Authorization: `Bearer ${activeToken}`,
                            },
                            timeout: 6000,
                        }
                    );
                    if (isMounted) {
                        setProductItems(itemsRes.data?.response || []);
                    }
                }
            } catch (err) {
                // Fallback cleanly
            } finally {
                if (isMounted) {
                    setLoadingProducts(false);
                }
            }
        };

        fetchCardProducts();

        return () => {
            isMounted = false;
        };
    }, [item.transaction_code, item.productItems, token]);

    // Don't display partially-loaded card; wait until products are ready or skeleton
    if (loadingProducts || productItems === null) {
        return <OrderCardSkeleton />;
    }

    const totalQty = productItems
        ? productItems.reduce((acc, p) => acc + (parseInt(p.qty, 10) || 1), 0)
        : 0;

    const handleCardClick = () => {
        if (item.transaction_code) {
            history.push(url.transactioncode(item.transaction_code));
        }
    };

    const renderCardStatusBadge = () => {
        if (isUnpaid) {
            if (timer.isExpired) {
                return (
                    <span className="order-status-badge status--rejected">
                        <i className="fas fa-ban" />
                        <span>Dibatalkan</span>
                    </span>
                );
            }
            return (
                <span className="order-status-badge status--unpaid">
                    <i className="far fa-clock" />
                    <span>{timer.formatted}</span>
                </span>
            );
        }

        switch (item.active_flag) {
            case 2:
                return (
                    <span className="order-status-badge status--paid">
                        <i className="fas fa-check-circle" />
                        <span>Lunas</span>
                    </span>
                );
            case 3:
                return (
                    <span className="order-status-badge status--dp">
                        <i className="fas fa-coins" />
                        <span>Uang Muka (DP)</span>
                    </span>
                );
            default:
                return (
                    <span className="order-status-badge status--cancelled">
                        <i className="fas fa-ban" />
                        <span>Dibatalkan</span>
                    </span>
                );
        }
    };

    return (
        <div
            className="order-item-card"
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleCardClick();
                }
            }}
        >
            {/* Card Header - date hidden on mobile so timer stays at top-right on the same line */}
            <div className="order-item-card__header">
                <div className="order-item-card__meta-left">
                    <Link
                        to={url.transactioncode(item.transaction_code)}
                        className="order-code-badge"
                        title="Lihat detail transaksi"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <i className="fas fa-receipt" />
                        <span>#{item.transaction_code}</span>
                    </Link>
                    <span className="order-date-text d-none d-md-inline-flex">
                        <i className="far fa-calendar-alt" />
                        <span>{item.transaction_date}</span>
                    </span>
                </div>
                <div className="order-item-card__status-wrap">{renderCardStatusBadge()}</div>
            </div>

            {/* Card Body */}
            <div className="order-item-card__body">
                <div className="order-detail-left">
                    {productItems && productItems.length > 0 ? (
                        <div className="order-product-main-info">
                            <div className="order-product-name-row" title={productItems[0].item_name}>
                                <i className="fas fa-box order-product-icon" />
                                <span className="product-name-truncate">{productItems[0].item_name}</span>
                                {productItems.length > 1 && (
                                    <span className="product-more-tag">+{productItems.length - 1} lainnya</span>
                                )}
                            </div>
                            <div className="order-meta-badges">
                                <span className="order-item-chip count-chip">
                                    <i className="fas fa-boxes" />
                                    <span><strong>{totalQty}</strong> Barang</span>
                                </span>
                                <span className="bank-chip">
                                    <i className="fas fa-university" />
                                    <span>Metode: <strong>{item.bank_name || 'BCA'}</strong></span>
                                </span>
                            </div>
                        </div>
                    ) : loadingProducts ? (
                        <div className="order-product-summary">
                            <span className="order-item-chip loading-chip">
                                <i className="fas fa-spinner fa-spin" />
                                <span>Memuat info pesanan...</span>
                            </span>
                        </div>
                    ) : (
                        <div className="order-product-main-info">
                            <div className="order-meta-badges">
                                <span className="bank-chip">
                                    <i className="fas fa-university" />
                                    <span>Metode: <strong>{item.bank_name || 'Transfer Bank'}</strong></span>
                                </span>
                                <span className="order-item-chip">
                                    <i className="fas fa-box-open" />
                                    <span>Rincian Barang</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Total Price Block on Desktop */}
                <div className="order-total-block d-none d-md-flex">
                    <span className="total-label">Total Pembayaran</span>
                    <span className="total-amount">{formatRupiah(item.transaction_total)}</span>
                </div>
            </div>

            {/* Card Footer */}
            <div className="order-item-card__footer">
                {/* Desktop hint text */}
                <span className="order-card-hint-text d-none d-md-inline-flex">
                    <i className="fas fa-arrow-right" />
                    <span>Klik card untuk lihat rincian lengkap</span>
                </span>

                {/* Mobile total price display on left */}
                <div className="mobile-order-total-box d-flex d-md-none">
                    <span className="mobile-total-label">Total Pembayaran</span>
                    <span className="mobile-total-amount">{formatRupiah(item.transaction_total)}</span>
                </div>

                {/* Action Button on right */}
                <div className="order-footer-actions">
                    {isUnpaid && (
                        timer.isExpired ? (
                            <button
                                type="button"
                                disabled
                                className="btn-order-action btn-order-disabled"
                                title="Batas waktu pembayaran habis. Pesanan telah dibatalkan."
                                onClick={(e) => e.stopPropagation()}
                            >
                                <i className="fas fa-credit-card" />
                                <span>Bayar Sekarang</span>
                            </button>
                        ) : (
                            <Link
                                className="btn-order-action btn-order-pay"
                                to={url.Pembayaran(item.id)}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <i className="fas fa-credit-card" />
                                <span>Bayar Sekarang</span>
                            </Link>
                        )
                    )}
                    {item.active_flag === 2 && (
                        <Link
                            className="btn-order-action btn-order-view"
                            to={url.transactioncode(item.transaction_code)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <i className="fas fa-file-invoice" />
                            <span>Rincian Pesanan</span>
                        </Link>
                    )}
                    {!isUnpaid && item.active_flag !== 2 && (
                        <Link
                            className="btn-order-action btn-order-view"
                            to={url.transactioncode(item.transaction_code)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <i className="fas fa-eye" />
                            <span>Lihat Rincian</span>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

function OrderCardSkeleton() {
    return (
        <div className="order-item-card order-item-card--skeleton" style={{ pointerEvents: 'none', cursor: 'default' }}>
            <div className="order-item-card__header">
                <div className="order-item-card__meta-left">
                    <span className="skeleton-bone" style={{ width: 140, height: 24, borderRadius: 6 }} />
                    <span className="skeleton-bone" style={{ width: 100, height: 18, borderRadius: 4 }} />
                </div>
                <span className="skeleton-bone skeleton-bone--pill" style={{ width: 110, height: 26 }} />
            </div>
            <div className="order-item-card__body">
                <div className="order-detail-left">
                    <span className="skeleton-bone" style={{ width: 160, height: 32, borderRadius: 8 }} />
                    <span className="skeleton-bone" style={{ width: 120, height: 32, borderRadius: 8 }} />
                </div>
                <div className="order-total-block">
                    <span className="skeleton-bone" style={{ width: 90, height: 14, borderRadius: 4, marginBottom: 6, marginLeft: 'auto' }} />
                    <span className="skeleton-bone" style={{ width: 120, height: 24, borderRadius: 6, marginLeft: 'auto' }} />
                </div>
            </div>
            <div className="order-item-card__footer">
                <span className="skeleton-bone" style={{ width: 180, height: 16, borderRadius: 4 }} />
                <span className="skeleton-bone" style={{ width: 125, height: 36, borderRadius: 10 }} />
            </div>
        </div>
    );
}

export default function AccountPageOrders() {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');

    const { token } = useSelector((state) => state.auth);
    const kodeuser = localStorage.getItem('user_code');

    useEffect(() => {
        let isMounted = true;

        const fetchOrders = async () => {
            try {
                setLoading(true);
                const result = await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_HOST_API}/transaction/page`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    data: {
                        page: page,
                        per_page: 15,
                        user_code: kodeuser,
                    },
                });

                const rawOrders = result.data?.response || [];

                // Pre-load all product details concurrently so everything renders complete without lag/jumping
                const enrichedOrders = await Promise.all(
                    rawOrders.map(async (order) => {
                        try {
                            if (!order.transaction_code) return { ...order, productItems: [] };
                            const detailRes = await axios.get(
                                `${process.env.REACT_APP_HOST_API}/transaction/detail/${order.transaction_code}`,
                                {
                                    headers: { Authorization: `Bearer ${token}` },
                                    timeout: 6000,
                                }
                            );
                            const soId = detailRes.data?.response?.soCommerceDetail?.id;
                            if (soId) {
                                const itemsRes = await axios.post(
                                    `${process.env.REACT_APP_HOST_API}/so-commerce/item`,
                                    { sc_id: soId },
                                    {
                                        headers: { Authorization: `Bearer ${token}` },
                                        timeout: 6000,
                                    }
                                );
                                return {
                                    ...order,
                                    productItems: itemsRes.data?.response || [],
                                };
                            }
                        } catch (err) {
                            // Fallback gracefully
                        }
                        return { ...order, productItems: [] };
                    })
                );

                if (isMounted) {
                    setOrders(enrichedOrders);
                    setLoading(false);
                }
            } catch (error) {
                if (isMounted) {
                    setOrders([]);
                    setLoading(false);
                }
            }
        };

        if (token) {
            fetchOrders();
        } else {
            setLoading(false);
        }

        return () => {
            isMounted = false;
        };
    }, [token, page, kodeuser]);

    const formatRupiah = (money) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(money || 0);
    };

    // Filter items based on selected tab
    const filteredOrders = orders.filter((item) => {
        if (filterStatus === 'all') return true;
        if (filterStatus === 'unpaid') return item.active_flag === 1;
        if (filterStatus === 'paid') return item.active_flag === 2;
        if (filterStatus === 'dp') return item.active_flag === 3;
        if (filterStatus === 'cancelled') return item.active_flag !== 1 && item.active_flag !== 2 && item.active_flag !== 3;
        return true;
    });

    const countUnpaid = orders.filter((o) => o.active_flag === 1).length;
    const countPaid = orders.filter((o) => o.active_flag === 2).length;

    const renderStatusBadge = (flag) => {
        switch (flag) {
            case 1:
                return (
                    <span className="order-status-badge status--unpaid">
                        <i className="fas fa-clock" />
                        <span>Segera Bayar</span>
                    </span>
                );
            case 2:
                return (
                    <span className="order-status-badge status--paid">
                        <i className="fas fa-check-circle" />
                        <span>Lunas</span>
                    </span>
                );
            case 3:
                return (
                    <span className="order-status-badge status--dp">
                        <i className="fas fa-coins" />
                        <span>Uang Muka (DP)</span>
                    </span>
                );
            default:
                return (
                    <span className="order-status-badge status--cancelled">
                        <i className="fas fa-ban" />
                        <span>Dibatalkan</span>
                    </span>
                );
        }
    };

    return (
        <div className="orders-page-wrapper">
            <Helmet>
                <title>{`Pesanan Produk | ${theme.name}`}</title>
            </Helmet>

            {/* Header Card (Hidden on mobile) */}
            <div className="orders-header-card d-none d-md-flex">
                <div className="orders-header__content">
                    <div className="orders-header__icon-box">
                        <i className="fas fa-shopping-bag" />
                    </div>
                    <div className="orders-header__text">
                        <h3>Daftar Pemesanan Produk</h3>
                        <p>Kelola riwayat pesanan Anda</p>
                    </div>
                </div>
                {orders.length > 0 && (
                    <div className="orders-header__badge">
                        <i className="fas fa-receipt" />
                        <span>{orders.length} Transaksi Ditemukan</span>
                    </div>
                )}
            </div>

            {/* Filter Tabs if orders exist */}
            {orders.length > 0 && (
                <div className="orders-filter-bar">
                    <button
                        type="button"
                        className={`orders-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        <span>Semua Pesanan</span>
                        <span className="filter-count">{orders.length}</span>
                    </button>
                    <button
                        type="button"
                        className={`orders-filter-btn ${filterStatus === 'unpaid' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('unpaid')}
                    >
                        <span>Menunggu Pembayaran</span>
                        {countUnpaid > 0 && <span className="filter-count">{countUnpaid}</span>}
                    </button>
                    <button
                        type="button"
                        className={`orders-filter-btn ${filterStatus === 'paid' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('paid')}
                    >
                        <span>Lunas / Selesai</span>
                        {countPaid > 0 && <span className="filter-count">{countPaid}</span>}
                    </button>
                </div>
            )}

            {/* Main Content Area */}
            {loading ? (
                <div className="orders-list">
                    <OrderCardSkeleton />
                    <OrderCardSkeleton />
                    <OrderCardSkeleton />
                </div>
            ) : filteredOrders.length > 0 ? (
                <div className="orders-list">
                    {filteredOrders.map((item) => (
                        <OrderItemCard
                            key={item.id}
                            item={item}
                            formatRupiah={formatRupiah}
                            token={token}
                        />
                    ))}
                </div>
            ) : (
                /* Empty State Card */
                <EmptyState
                    icon="box-open"
                    title={filterStatus !== 'all' ? 'Tidak ada pesanan' : 'Belum ada pesanan'}
                    text={filterStatus !== 'all' ? 'Tidak ada pesanan pada filter ini.' : 'Pesananmu akan tampil di sini.'}
                    actionLabel="Mulai Belanja"
                    actionTo="/"
                    actionIcon="shopping-bag"
                />
            )}
        </div>
    );
}